import { getCommonAxiosCommon } from 'app/helpers/MiscUtils';
import { NgAPIResponse, Sponsor } from 'app/types';
import axios from 'axios';
import { config } from 'config';

export type CompletionStepType = 'FilledOrganizationDetails' | 'CreatedJobFair' | 'JobFairPaymentCompleted' | 'BoothConfigured' | 'AssignedARecruiter';

export interface Organization {
  id: string;
  companyName: string;
  city: string;
  state: string;
  companyWebsite: string;
  companyDetails: string;
  jobBoardLink: string;
  companyLogo: string;
  companyDocuments: string[];
  sponsorId: string;
  sponsorName: string;
  createdAt: string;
  createdBy: number;
  onBoardingProgress: { progress: number, lastCompletedStep: CompletionStepType };
}

export interface WebhookOrgJobFairInfo {
  orgName: string;
  isActivated?: boolean;
  jobFairEvent: {
    startDate: string;
    startTime: string;
    timezone: string;
    durationHours: number;
    durationMinutes: number;
  };
  sponsor?: Sponsor;
}

export class OrgService {
  private baseUrl = `${config.vjfpNGRestAPI}/organizations`;
  private orgJobfairInfo = `${config.vjfpNGRestAPI}/public/org-jobfair-info`;
  async createOrg (orgData: Partial<Organization>): Promise<Organization> {
    const result = await axios.post<NgAPIResponse<Organization>>(
      this.baseUrl,
      orgData,
      await getCommonAxiosCommon()
    );

    if (!result.data?.isSuccess) {
      console.error(result.data.errors);
      throw new Error('Failed to create organization');
    }

    return result.data.data;
  }

  async getAllOrganizations (limit?: number, sortBy?: 'date' | 'companyName', sortDirection?: 'asc' | 'desc', cursor?: string): Promise<{ organizations: Organization[], cursor?: string }> {
    const params = new URLSearchParams();
    if (limit) {
      params.append('limit', limit.toString());
    }
    if (sortBy) {
      params.append('sortBy', sortBy);
    }
    if (sortDirection) {
      params.append('sortDirection', sortDirection);
    }
    if (cursor) {
      params.append('cursor', cursor);
    }

    const paramsStr = params.toString();

    const url = `${this.baseUrl}${paramsStr ? '?' + paramsStr : ''}`;
    const result = await axios.get<NgAPIResponse<{ items: Organization[], cursor?: string }>>(
      url,
      await getCommonAxiosCommon()
    );

    if (!result.data?.isSuccess) {
      console.error(result.data.errors);
      throw new Error('Failed to get organizations');
    }

    return {
      organizations: result.data.data.items,
      cursor: result.data.data.cursor
    };
  }

  async getOrgById<Organization> (orgId: string): Promise<Organization> {
    const result = await axios.get<NgAPIResponse<Organization>>(
      `${this.baseUrl}/${orgId}`,
      await getCommonAxiosCommon()
    );

    if (!result.data?.isSuccess) {
      console.error(result.data.errors);
      throw new Error('Failed to get organization');
    }

    return result.data.data;
  }

  async getWebhookOrgJobFairInfo (orgId: string, jobFairId: string): Promise<WebhookOrgJobFairInfo> {
    const result = await axios.get<NgAPIResponse<WebhookOrgJobFairInfo>>(
      `${this.orgJobfairInfo}/${orgId}/${jobFairId}`);
    if (!result.data?.isSuccess) {
      console.error(result.data.errors);
      throw new Error('Failed to get organization');
    }

    return result.data.data;
  }

  async updateOrg (orgId: string, orgData: Partial<Organization>): Promise<Organization> {
    const result = await axios.put<NgAPIResponse<Organization>>(
      `${this.baseUrl}/${orgId}`,
      orgData,
      await getCommonAxiosCommon()
    );

    if (!result.data?.isSuccess) {
      console.error(result.data.errors);
      throw new Error('Failed to update organization');
    }

    return result.data.data;
  }

  async deleteOrg (orgId: string): Promise<void> {
    const result = await axios.delete<NgAPIResponse<void>>(
      `${this.baseUrl}/${orgId}`,
      await getCommonAxiosCommon()
    );

    if (!result.data?.isSuccess) {
      console.error(result.data.errors);
      throw new Error('Failed to delete organization');
    }
  }
}
